import { Grid, Chip, CircularProgress, TextField } from "@mui/material";
import { memo, useEffect, useState } from "react";
import { formatTimeFromUtc } from "utils/formatters";
import { getYearsFromUtc } from "utils/general";
import { athleteInfoText, editableContainer, editText } from "./styles";
import { capitalize } from "utils/formatters";
import { MultiSelect } from "components/SportSelect/MultiSelect";
import CloseIcon from "@mui/icons-material/Close";
import { getCoachTeams } from "repositories/teamsApi";
import { PatientRepository } from "repositories/PatientRepository";
import { POSITIONS } from "constants/positions";
interface Team {
  _id: string;
  name: string;
}

const EditableField = ({
  isTextField,
  editMode,
  field,
  setEditingField,
  value,
  setNewValues,
  newValues,
  options,
  loading,
  onSave,
  menuSubTitle = "(M = Men's, W = Women's)",
}: {
  isTextField?: boolean;
  editMode: any;
  field: any;
  setEditingField: any;
  loading: any;
  value: any;
  setNewValues: any;
  newValues: any;
  currentClinician: any;
  menuSubTitle?: any;
  options?: any;
  onSave: any;
}) => {
  const iterable = isTextField ? [newValues] : newValues;
  const doNotShowSelected =
    (isTextField && newValues === "") || typeof newValues === "string";
  return (
    <p>
      <div style={editableContainer}>
        <strong>{capitalize(field)}</strong>{" "}
        <p
          onClick={() => {
            if (editMode) {
              onSave();
              setEditingField("");
            } else {
              setEditingField(field);
            }
          }}
          style={editText}
        >
          {loading ? <CircularProgress /> : editMode ? "Save Changes" : "Edit"}
        </p>
      </div>
      {editMode ? (
        <div
          style={{
            marginBottom: "20px",
          }}
        >
          <MultiSelect
            options={options ?? []}
            selectedOptions={iterable}
            onItemSelected={(_: any, value: string) => {
              if (isTextField) {
                return setNewValues(value);
              }

              const sportExists = newValues?.find(
                (selectedValue: string) => selectedValue === value
              );
              if (sportExists) {
                return setNewValues(
                  newValues.filter(
                    (selectedValue: string) => selectedValue !== value
                  )
                );
              } else {
                setNewValues([...newValues, value]);
              }
            }}
            paddingTop={0}
            hint={`${capitalize(field)} list`}
            menuSubTitle={menuSubTitle}
            menuTitle={""}
            style={{
              border: "1px",
              height: "34px",
              borderRadius: "10px",
              backgroundColor: "#FFFFFF",
              borderColor: "#E5E5E5",
              fontSize: "14px",
              color: "#5C5D69",
              width: "100%",
              marginTop: "20px",
            }}
            emptyMessage={
              field == "sports"
                ? "Please select sport categories in settings page"
                : field == "teams"
                ? "Create teams in settings page"
                : "Select position"
            }
          />

          {!doNotShowSelected &&
            iterable.map((tag: string) => (
              <Grid item justifyContent="space-between">
                <Chip
                  key={tag}
                  style={{
                    backgroundColor: "#161728",
                    borderRadius: 10,
                    margin: 3,
                    color: "#FFFFFF",
                  }}
                  deleteIcon={
                    <CloseIcon
                      style={{
                        color: "white",
                        fontSize: "1.2em",
                        stroke: "white",
                        strokeWidth: 2,
                      }}
                    />
                  }
                  label={tag}
                  variant="outlined"
                  onDelete={() => {
                    setNewValues(
                      newValues.filter(
                        (selectedValue: string) => selectedValue !== tag
                      )
                    );
                  }}
                />
              </Grid>
            ))}
        </div>
      ) : (
        <p style={athleteInfoText}>
          {
            <Grid md={12} xl={12} container>
              {isTextField || typeof value === "string" ? (
                <Grid item>{value}</Grid>
              ) : (
                <Grid item>{value.join(", ")}</Grid>
              )}
            </Grid>
          }
        </p>
      )}
    </p>
  );
};

type Props = {
  firstName: string;
  lastName: string;
  uniqueId: string;
  patientImage: string;
  dateOfBirth: string;
  email?: string;
  gender?: string;
  phone?: string;
  sportPosition?: string[];
  sports?: string[];
  teams?: string[];
  currentClinician: any;
  status?: string;
  inviteDates?: string[];
};

export const AthleteInfo = memo(
  ({
    firstName,
    lastName,
    uniqueId,
    patientImage,
    dateOfBirth,
    email,
    gender,
    phone,
    sports,
    teams,
    status,
    inviteDates,
    sportPosition,
    currentClinician,
  }: Props) => {
    const [style, setStyle] = useState({});
    const [imgAlt, setImgAlt] = useState("");
    const [editingField, setEditingField] = useState("");
    const [loading, setLoading] = useState("");
    const [newSports, setNewSports] = useState(sports);
    const [newPosition, setNewPosition] = useState(
      typeof sportPosition === "string" ? [sportPosition] : sportPosition
    );
    const [newTeams, setNewTeams] = useState<any[]>([]);
    const [teamList, setTeamList] = useState([]);
    const [fullTeams, setFullTeams] = useState<Team[]>([]);

    useEffect(() => {
      if (firstName?.length > 0 && lastName?.length > 0) {
        setImgAlt(firstName[0] + lastName[0]);
      } else if (firstName?.length > 0) {
        setImgAlt(firstName[0]);
      } else if (lastName?.length > 0) {
        setImgAlt(lastName[0]);
      }
      if (!patientImage) {
        setStyle({
          lineHeight: "93px",
          textAlign: "center",
          fontSize: "38px",
          width: "90px",
          position: "relative",
          left: "-4px",
        });
      }
      return () => {
        setImgAlt("");
        setStyle({});
      };
    }, [firstName, lastName]);

    const getTeams = async () => {
      if (!currentClinician) return;
      try {
        const fetchedTeams = await getCoachTeams(currentClinician._id);
        const teamNames = fetchedTeams.map((team: any) => team.name);
        setTeamList(teamNames);
        setFullTeams(fetchedTeams);

        if (teams?.length) {
          const selectedTeams = teams
            .map((team: any) => {
              const foundTeam = fetchedTeams.find(
                (teamItem: any) => teamItem._id === team
              );
              return foundTeam?.name;
            })
            .filter((teamName: any) => teamNames.includes(teamName));
          setNewTeams(selectedTeams);
        }
      } catch (error) {
        setTeamList([]);
      }
    };

    useEffect(() => {
      getTeams();
    }, []);

    const onSave = async () => {
      const patientRepo = PatientRepository.getInstance();

      if (editingField === "sports") {
        setLoading("sports");
        await patientRepo.updatePatient(uniqueId, {
          sports: newSports,
        });
        setLoading("");
      }
      if (editingField === "position") {
        setLoading("position");
        await patientRepo.updatePatient(uniqueId, {
          position: newPosition,
        });
        setLoading("");
      }
      if (editingField === "teams") {
        setLoading("teams");
        const teamIds = [];
        for (const team of newTeams || []) {
          const foundTeam = fullTeams.find(
            (teamItem: Team) => teamItem.name === team
          );
          if (foundTeam) {
            teamIds.push(foundTeam?._id);
          }
        }
        await patientRepo.updatePatient(uniqueId, {
          teams: teamIds,
        });
        setLoading("");
      }
    };

    return (
      <div className="w-full flex card rounded-[20px] pt-7 justify-between pb-7 px-8 md:flex-col md:space-y-8">
        <div className="flex space-x-7 sm:flex-col sm:space-x-0 sm:space-y-4">
          <div className="w-[100px] h-[100px] rounded-full bg-grayDark overflow-hidden shrink-0">
            <img
              className="object-cover min-w-full min-h-full"
              src={patientImage || ""}
              alt={imgAlt}
              style={style}
            />
          </div>
          <div className="flex flex-col items-start min-h-[125px]">
            <h4 className="text-grayDark">Athlete ID#: {uniqueId}</h4>
            <p className="text-[38px] font-black">
              {firstName} {lastName}
            </p>
          </div>
        </div>

        <div className="flex md:min-h-[100px] sm:flex-col ">
          <div className="flex flex-col  pr-10 border-r border-r-border sm:border-none">
            <p>
              <strong>DOB</strong>
              <p style={athleteInfoText}>
                {formatTimeFromUtc(dateOfBirth, "M/d/yyyy")}
              </p>
            </p>
            <p>
              <strong>Age</strong>
              <p style={athleteInfoText}>{getYearsFromUtc(dateOfBirth)}</p>
            </p>
            <p>
              <strong>Gender</strong>
              <p style={athleteInfoText}>{gender ?? "-"}</p>
            </p>
            <p>
              <strong>Dates invited</strong>
              <p style={athleteInfoText}>
                <Grid md={12} xl={12} container>
                  {inviteDates && inviteDates.length > 0 && (
                    <Grid item>{inviteDates.join(", ")}</Grid>
                  )}
                </Grid>
              </p>
            </p>
            <p>
              <strong>Status</strong>
              <p style={athleteInfoText}>{capitalize(status || "") ?? ""}</p>
            </p>
          </div>
          <div className="flex flex-col justify-between pl-10 sm:pl-0">
            <p>
              <strong>Email</strong>
              <p style={athleteInfoText}>{email}</p>
            </p>
            <p>
              <strong>Phone</strong>
              <p style={athleteInfoText}>{phone}</p>
            </p>
            <EditableField
              onSave={onSave}
              editMode={editingField === "sports"}
              field="sports"
              options={currentClinician?.profileInfo?.sports}
              setEditingField={setEditingField}
              value={newSports}
              loading={loading === "sports"}
              setNewValues={setNewSports}
              newValues={newSports}
              currentClinician={currentClinician}
            />
            <EditableField
              options={teamList}
              onSave={onSave}
              loading={loading === "teams"}
              editMode={editingField === "teams"}
              field="teams"
              setEditingField={setEditingField}
              value={newTeams}
              setNewValues={setNewTeams}
              newValues={newTeams}
              currentClinician={currentClinician}
            />
            <EditableField
              onSave={onSave}
              editMode={editingField === "position"}
              field={"position"}
              options={POSITIONS}
              setEditingField={setEditingField}
              value={newPosition}
              loading={loading === "position"}
              setNewValues={setNewPosition}
              newValues={newPosition}
              currentClinician={currentClinician}
            />
          </div>
        </div>
      </div>
    );
  }
);
